import type { ReactNode } from 'react';
import { honestyOf, shownHonesty, type HonestyFlags, type HonestyLabel } from '@aithora/shared';
import { useDemoBuilding } from '../../session/DemoScope';
import { sx as designSx } from '../../ui/designStyles';
import { HonestyBadge } from '../../ui/HonestyBadge';
import type { Ax } from './model';
import { useBuildLabels } from '../../ui/buildLabels';

/** The design's inline style strings as React styles. */
export const sx = designSx;

/** An anchor-styled button: the design draws these as <a href="#"> with its own style. */
export function A({
  style,
  onClick,
  children,
  disabled,
  title,
  label,
  dataK,
  testId,
  ink,
}: {
  style: string;
  onClick: () => void;
  children: ReactNode;
  disabled?: boolean;
  title?: string;
  label?: string;
  dataK?: string;
  testId?: string;
  /** White words on a filled button: they stay white in Dark (theme-dark.css .cf-ink). */
  ink?: boolean;
}) {
  return (
    <button
      type="button"
      className={ink ? 'wl-a cf-ink' : 'wl-a'}
      style={sx(style)}
      onClick={onClick}
      disabled={disabled}
      aria-disabled={disabled}
      title={title}
      aria-label={label}
      data-k={dataK}
      data-testid={testId}
    >
      {children}
    </button>
  );
}

/**
 * An honesty label the design writes inline ("SAMPLE residents", "MODELED"), drawn in the
 * design's own style but worded only by the API's flags. No flag: nothing (and the
 * development marker from HonestyBadge, so the missing flag gets fixed in the API).
 */
export function HonestyText({ flags, figure, style, suffix, title, hide = [] }: { flags: (HonestyFlags | null | undefined)[]; figure: string; style: string; suffix?: string; title?: string; hide?: HonestyLabel[] }) {
  const demoBuilding = useDemoBuilding();
  const buildLabels = useBuildLabels();
  const label = honestyOf(...flags);
  if (!buildLabels) return null;
  if (!label) return <HonestyBadge flags={flags} figure={figure} />;
  if (hide.includes(label)) return null;
  // In a seeded building scope (no banner, no "demo" on screen): no SAMPLE word here.
  if (shownHonesty(label, demoBuilding) === null) return null;
  return (
    <span title={title} style={sx(style)} data-honesty={label}>
      {label}
      {suffix}
    </span>
  );
}

export interface AxActions {
  onSel: (id: string) => void;
  onDone: (ax: Ax) => void;
  onMenu: (id: string, kind: 'snz' | 'hand' | 'ref') => void;
  onPick: (ax: Ax, kind: 'snz' | 'hand' | 'ref', i: number) => void;
  onUndo: (ax: Ax) => void;
  menu: { id: string; kind: 'snz' | 'hand' | 'ref' } | null;
  menuPicks: (kind: 'snz' | 'hand' | 'ref', ax?: Ax) => string[];
  disabled: boolean;
  disabledReason: string | null;
}

/**
 * The line every open menu carries (design wlLayer mk() `ladder`): what the server's task
 * ladder does (v3/worklist/ladder-rule.ts: the V7 rule and its default minutes).
 */
export const AX_LADDER = 'If nobody does it, it moves on by itself: the charge nurse, then the on-call nurse, then an urgent alert to everyone on shift. Urgent moves every 2 min, soon after 10 then 5, routine after 30 then 15 (no on-call step); your facility can change these. Snoozing keeps it yours; the clock keeps running.';

/** The action strip every row carries (design wlLayer mk(), T1539). */
export function AxView({ ax, act }: { ax: Ax; act: AxActions }) {
  const menu = act.menu && act.menu.id === ax.id ? act.menu.kind : null;
  const off = act.disabled;
  const t = (tip: string) => act.disabledReason ?? tip;
  if (!ax.show) return null;
  return (
    <>
      <span data-ax={ax.id} style={sx(ax.wrap)}>
        <A style={ax.selStyle} dataK="x" title={t('Select (X)')} label={ax.selected ? `Deselect ${ax.who}` : `Select ${ax.who}`} onClick={() => act.onSel(ax.id)} disabled={off}>
          {ax.selMark}
        </A>
        {ax.open && (
          <>
            {ax.hasDone && (
              <A style={ax.doneStyle} dataK="d" title={t(ax.tipD)} onClick={() => act.onDone(ax)} disabled={off}>
                Done
              </A>
            )}
            <A style={ax.btnStyle} dataK="s" title={t(ax.tipS)} onClick={() => act.onMenu(ax.id, 'snz')} disabled={off}>
              Snooze &#9662;
            </A>
            <A style={ax.btnStyle} dataK="h" title={t(ax.tipH)} onClick={() => act.onMenu(ax.id, 'hand')} disabled={off}>
              Hand off &#9662;
            </A>
            <A style={ax.btnStyle} dataK="r" title={t(ax.tipR)} onClick={() => act.onMenu(ax.id, 'ref')} disabled={off}>
              Refused &#9662;
            </A>
          </>
        )}
        {ax.hasState && (
          <>
            <span style={sx(ax.stateStyle)}>{ax.stateText}</span>
            {ax.canUndo && (
              <A style="font-size:16px;font-weight:700;color:#2F5C97;text-decoration:none;white-space:nowrap;" onClick={() => act.onUndo(ax)} disabled={off} title={act.disabledReason ?? undefined}>
                Undo
              </A>
            )}
          </>
        )}
        {ax.risk && (
          <span title={ax.riskWhy} style={sx('font-size:14px;font-weight:800;letter-spacing:.06em;color:#B3261E;white-space:nowrap;')}>
            AT RISK
          </span>
        )}
      </span>
      {menu && (
        <span style={sx('flex:1 1 100%;grid-column:1/-1;display:flex;align-items:center;justify-content:flex-end;gap:6px;flex-wrap:wrap;min-width:0;padding:4px 0 2px;')}>
          <span style={sx('flex:1 1 100%;display:block;font-size:14px;line-height:1.45;color:#54606C;margin-bottom:4px;')}>{AX_LADDER}</span>
          <span style={sx('font-size:14px;font-weight:700;color:#54606C;')}>{menu === 'snz' ? 'Snooze because' : menu === 'hand' ? 'Hand to' : 'Refused because'}</span>
          {act.menuPicks(menu, ax).map((label, i) => (
            <A
              key={label}
              style="display:inline-flex;align-items:center;min-height:40px;padding:0 13px;border-radius:8px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;color:#264C7E;border:1px solid rgba(47,92,151,.4);"
              onClick={() => act.onPick(ax, menu, i)}
              disabled={off}
            >
              {label}
            </A>
          ))}
        </span>
      )}
    </>
  );
}

/** A count strip cell as the tabs draw it: big number, then its word. */
export function CountCell({ v, k, c, size = 24, gap = 6, kColor = '#54606C', kWeight = 600, inline = false, title }: { v: string; k: string; c: string; size?: number; gap?: number; kColor?: string; kWeight?: number; inline?: boolean; title?: string }) {
  return (
    <span title={title} style={sx(`display:${inline ? 'inline-flex' : 'flex'};align-items:baseline;gap:${gap}px;`)}>
      <span data-wq-count={v} style={sx(`font-size:${size}px;font-weight:800;letter-spacing:${size === 22 && inline ? '-.03em' : '-.02em'};font-variant-numeric:tabular-nums;color:${c};`)}>{v}</span>
      <span style={sx(`font-size:16px;color:${kColor};${kWeight ? `font-weight:${kWeight};` : ''}`)}>{k}</span>
    </span>
  );
}
